import { useEffect, useRef, useState } from "react";
import { askScenarios, askSuggestionOrder, type AskScenario } from "../lib/data";
import { quotesForTheme, useMgQual, useQuoteBank, type Quote } from "../lib/realdata";

type Phase = "idle" | "thinking" | "streaming" | "done";

const DEFAULT_SCENARIO_ID = askSuggestionOrder[0];

export default function Ask() {
  const [phase, setPhase] = useState<Phase>("idle");
  const [scenarioId, setScenarioId] = useState<string>(DEFAULT_SCENARIO_ID);
  const [thinkingIdx, setThinkingIdx] = useState(0);
  const [streamed, setStreamed] = useState("");
  const [showChart, setShowChart] = useState(false);
  const [showCites, setShowCites] = useState(false);
  const [activeCite, setActiveCite] = useState<number | null>(null);
  const [input, setInput] = useState("");
  const timers = useRef<number[]>([]);

  const scenario: AskScenario = askScenarios[scenarioId];

  useEffect(() => () => { timers.current.forEach(clearTimeout); }, []);

  const clearTimers = () => { timers.current.forEach(clearTimeout); timers.current = []; };

  // Run a scenario by id. If an input was typed that doesn't match a scenario,
  // fall back to the scenario whose question is most similar, otherwise scenario 1.
  const resolveScenarioId = (typed: string): string => {
    if (!typed.trim()) return DEFAULT_SCENARIO_ID;
    const t = typed.toLowerCase();
    // exact/suggestion match
    for (const id of askSuggestionOrder) {
      const sc = askScenarios[id];
      if (t.includes(sc.shortLabel.toLowerCase().slice(0, 20))) return id;
    }
    // keyword heuristics so typed questions land sensibly in a demo
    if (/(durab|jar|qual|kolkata.*concern|respond)/i.test(typed)) return "durability-kol";
    if (/(segment|psychograph|intent|aspiration)/i.test(typed)) return "segment-intent";
    if (/(social|amazon|review.*match|survey.*match|triangul)/i.test(typed)) return "signal-drift";
    if (/(nps|wave|compare|preethi.*bajaj)/i.test(typed)) return "nps-wave";
    return DEFAULT_SCENARIO_ID;
  };

  const run = (idOrTyped?: string) => {
    clearTimers();
    let targetId = DEFAULT_SCENARIO_ID;
    let typedForInput = "";
    if (idOrTyped && askScenarios[idOrTyped]) {
      targetId = idOrTyped;
      typedForInput = askScenarios[idOrTyped].question;
    } else if (idOrTyped) {
      targetId = resolveScenarioId(idOrTyped);
      typedForInput = idOrTyped;
    } else {
      typedForInput = askScenarios[DEFAULT_SCENARIO_ID].question;
    }

    setScenarioId(targetId);
    setInput(typedForInput);
    setPhase("thinking");
    setThinkingIdx(0);
    setStreamed("");
    setShowChart(false);
    setShowCites(false);
    setActiveCite(null);

    const sc = askScenarios[targetId];
    const steps = sc.thinkingSteps;
    const perStep = 580;
    for (let i = 1; i < steps.length; i++) {
      timers.current.push(window.setTimeout(() => setThinkingIdx(i), perStep * i));
    }
    const total = perStep * steps.length;
    timers.current.push(window.setTimeout(() => {
      setPhase("streaming");
      streamAnswer(sc);
    }, total));
  };

  const streamAnswer = (sc: AskScenario) => {
    const full = sc.headline;
    const charStep = 10;
    let i = 0;
    const tick = () => {
      i += 2;
      setStreamed(full.slice(0, i));
      if (i < full.length) {
        timers.current.push(window.setTimeout(tick, charStep));
      } else {
        timers.current.push(window.setTimeout(() => setShowChart(true), 220));
        timers.current.push(window.setTimeout(() => setShowCites(true), 480));
        timers.current.push(window.setTimeout(() => setPhase("done"), 720));
      }
    };
    tick();
  };

  const reset = () => {
    clearTimers();
    setPhase("idle");
    setStreamed("");
    setShowChart(false);
    setShowCites(false);
    setActiveCite(null);
    setInput("");
    setThinkingIdx(0);
  };

  const sidePanelCite = activeCite != null ? scenario.citations.find((c) => c.n === activeCite) : null;

  return (
    <div className="stagger">
      <div className="ask-shell">
        <div className="ask-panel">
          <div className="ask-header">
            <div>
              <h1 className="h-page">Ask Pulse</h1>
              <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>Anything about your brand, your competitors, or what your consumers are saying.</div>
            </div>
            <div className="sub-tag">
              <span className="dot" /> Responses are cited to source
            </div>
          </div>

          <div className="thread">
            {phase === "idle" ? (
              <div className="idle-hint">
                <h3>What would you like to ask?</h3>
                <p>Pulse cites every number it returns. If a source does not support the answer, it will say so rather than guess.</p>

                <div className="eyebrow" style={{ marginBottom: 10 }}>Suggested starts</div>
                <div className="suggestion-stack">
                  {askSuggestionOrder.map((id) => {
                    const sc = askScenarios[id];
                    const kindTag = kindForScenario(id);
                    return (
                      <div key={id} className="sug-row" onClick={() => run(id)}>
                        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span className={"tag " + kindTag.tone} style={{ marginRight: 4, flexShrink: 0 }}>{kindTag.label}</span>
                          <span>{sc.shortLabel}</span>
                        </span>
                        <span className="arrow">→</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                <div className="msg-user">
                  <span className="by">You · just now</span>
                  {input}
                </div>

                <div className="msg-assistant">
                  <div className="by"><span className="mk">P</span>Pulse</div>

                  {phase === "thinking" && (
                    <div className="think-list">
                      {scenario.thinkingSteps.map((s, i) => {
                        const isDone = i < thinkingIdx;
                        const isActive = i === thinkingIdx;
                        return (
                          <div key={i} className={"think-row " + (isDone ? "done" : isActive ? "active" : "")}>
                            <span className="tick" />
                            <span className="txt">
                              {s}
                              {isActive && <span className="type-caret" />}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {(phase === "streaming" || phase === "done") && (
                    <>
                      <div className="a-headline">
                        {phase === "done" ? (
                          scenario.headlineRich
                        ) : (
                          <>{streamed}<span className="type-caret" /></>
                        )}
                      </div>

                      {showChart && scenario.chart && (
                        <div className="chart-wrap" style={{ animation: "fadeUp 360ms cubic-bezier(.2,.7,.2,1)" }}>
                          <ChartFor scenario={scenario} />
                        </div>
                      )}

                      {showCites && (
                        <>
                          <div className="eyebrow" style={{ marginTop: 18, marginBottom: 8 }}>Sources · click to inspect</div>
                          <div className="cite-row">
                            {scenario.citations.map((c) => (
                              <div
                                key={c.n}
                                className={"cite " + (c.kind === "Qual" ? "qual " : c.kind === "Social" ? "social " : "") + (activeCite === c.n ? "active" : "")}
                                onClick={() => setActiveCite(c.n)}
                              >
                                <span className="n">{c.n}</span>
                                <span className="k">{c.kind}</span>
                                {c.label}
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="ask-input-row">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about Preethi, durability, Kolkata, NPS, reviews…"
              onKeyDown={(e) => { if (e.key === "Enter" && input.trim() && phase !== "thinking" && phase !== "streaming") run(input); }}
            />
            {(phase === "idle" || phase === "done") ? (
              <button className="btn-primary" onClick={() => run(input || undefined)}>
                {phase === "done" ? "Ask again" : "Ask"}
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m13 6 6 6-6 6"/></svg>
              </button>
            ) : (
              <button className="btn-secondary" onClick={reset}>Cancel</button>
            )}
          </div>
        </div>

        <aside className="ask-side">
          <SidePanel scenario={scenario} activeCite={sidePanelCite} phase={phase} />
        </aside>
      </div>
    </div>
  );
}

function SidePanel({
  scenario, activeCite, phase,
}: {
  scenario: AskScenario;
  activeCite: ReturnType<AskScenario["citations"]["find"]> | null | undefined;
  phase: string;
}) {
  const bank = useQuoteBank();

  // Case A: citation has scripted transcript lines -> show them.
  if (activeCite && activeCite.detailLines) {
    return (
      <>
        <h4>Evidence</h4>
        <div className="transcript-meta"><span className="dot" />{activeCite.kind} transcript</div>
        <div className="transcript-title">{activeCite.detailTitle}</div>
        <div className="transcript">
          {activeCite.detailLines.map((ln, i) => (
            <p key={i}><span className="m">{ln.who}:</span>{ln.mark ? <mark>{ln.text}</mark> : ln.text}</p>
          ))}
        </div>
        {scenario.related && (
          <div className="related">
            <h4>Related questions</h4>
            <ul>{scenario.related.map((r) => <li key={r}>{r}</li>)}</ul>
          </div>
        )}
      </>
    );
  }

  // Case B: citation refers to a quote-bank theme -> render real quotes.
  if (activeCite && activeCite.quoteTheme && bank) {
    const quotes = quotesForTheme(bank, activeCite.quoteTheme, 3);
    return (
      <>
        <h4>Evidence · {activeCite.quoteTheme}</h4>
        <div className="transcript-meta">
          <span className="dot" />
          {quotes.length ? `${quotes.length} representative quotes · drawn from 18 DIs` : "No matching quotes in bank"}
        </div>
        {quotes.length === 0 ? (
          <div className="side-empty">No respondent turns matched this theme in the quote bank. Pulse will not fabricate one.</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 10 }}>
            {quotes.map((q, i) => <QuoteCard key={i} q={q} />)}
          </div>
        )}
        {scenario.related && (
          <div className="related">
            <h4>Related questions</h4>
            <ul>{scenario.related.map((r) => <li key={r}>{r}</li>)}</ul>
          </div>
        )}
      </>
    );
  }

  // Case C: done but no citation active -> show People-also-ask.
  if (phase === "done" && scenario.related) {
    return (
      <>
        <h4>Evidence</h4>
        <div className="side-empty">
          <svg viewBox="0 0 24 24"><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"/></svg>
          Click any source chip on the answer to see the exact question, row, or transcript line Pulse used.
        </div>
        <div className="related">
          <h4>People also ask</h4>
          <ul>{scenario.related.map((r) => <li key={r}>{r}</li>)}</ul>
        </div>
      </>
    );
  }

  // Default idle state.
  return (
    <>
      <h4>Evidence</h4>
      <div className="side-empty">
        <svg viewBox="0 0 24 24"><path d="M14 3v4a1 1 0 0 0 1 1h4"/><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z"/></svg>
        Click any source chip on an answer to see the exact question, row, or transcript line Pulse used.
      </div>
    </>
  );
}

function QuoteCard({ q }: { q: Quote }) {
  return (
    <div style={{
      padding: "12px 14px",
      background: "var(--card-2)",
      border: "1px solid var(--line)",
      borderLeft: "3px solid var(--amber)",
      borderRadius: "var(--r-2)",
    }}>
      <div style={{ fontSize: 13.5, lineHeight: 1.55, color: "var(--ink-2)" }}>
        <span style={{ color: "var(--ink-40)", marginRight: 4 }}>"</span>
        {q.text}
        <span style={{ color: "var(--ink-40)", marginLeft: 2 }}>"</span>
      </div>
      <div style={{ fontSize: 11, color: "var(--ink-60)", marginTop: 8, fontFamily: "var(--mono)", letterSpacing: "0.02em" }}>
        {q.respondent} · DI {q.di} · {q.city} · {q.moderator}
      </div>
    </div>
  );
}

function kindForScenario(id: string): { label: string; tone: string } {
  switch (id) {
    case "nps-wave":       return { label: "Quant trend",   tone: "teal" };
    case "durability-kol": return { label: "Qual synthesis", tone: "amber" };
    case "segment-intent": return { label: "Segmentation",   tone: "mint" };
    case "signal-drift":   return { label: "Triangulation",  tone: "rose" };
    default:               return { label: "Ask",            tone: "teal" };
  }
}

/* =========================== CHARTS =========================== */

function ChartFor({ scenario }: { scenario: AskScenario }) {
  switch (scenario.chart) {
    case "trend":      return <TrendChart />;
    case "qualBars":   return <QualBars />;
    case "segments":   return <SegmentBars />;
    case "divergence": return <Divergence />;
    default: return null;
  }
}

function ChartFrame({ title, legend, children }: { title: string; legend?: React.ReactNode; children: React.ReactNode }) {
  return (
    <>
      <div className="hd">
        <span className="title">{title}</span>
        {legend && <span className="legend">{legend}</span>}
      </div>
      {children}
    </>
  );
}

function TrendChart() {
  return (
    <ChartFrame title="NPS · Tier 2 · Wave 1 → Wave 2" legend={
      <>
        <span><i style={{ background: "var(--teal)" }} />Preethi</span>
        <span><i style={{ background: "var(--ink-40)" }} />Bajaj</span>
      </>
    }>
      <svg viewBox="0 0 640 180" width="100%" height="170" style={{ marginTop: 8 }}>
        <line x1="50" y1="20" x2="50" y2="150" stroke="var(--line)" />
        <line x1="50" y1="150" x2="620" y2="150" stroke="var(--ink-20)" />
        <text x="44" y="26" textAnchor="end" fontSize="10" fill="var(--ink-40)" fontFamily="JetBrains Mono">+60</text>
        <text x="44" y="92" textAnchor="end" fontSize="10" fill="var(--ink-40)" fontFamily="JetBrains Mono">+30</text>
        <text x="44" y="150" textAnchor="end" fontSize="10" fill="var(--ink-40)" fontFamily="JetBrains Mono">0</text>
        <text x="150" y="172" textAnchor="middle" fontSize="11" fill="var(--ink-60)">Wave 1</text>
        <text x="520" y="172" textAnchor="middle" fontSize="11" fill="var(--ink-60)">Wave 2</text>
        <line x1="50" y1="92" x2="620" y2="92" stroke="var(--line-2)" strokeDasharray="3 3" />
        <path d="M 150 68 Q 335 56 520 42" fill="none" stroke="var(--teal)" strokeWidth="2.5" />
        <circle cx="150" cy="68" r="5" fill="var(--teal)" />
        <circle cx="520" cy="42" r="5" fill="var(--teal)" />
        <text x="140" y="61" textAnchor="end" fontSize="11" fill="var(--teal-ink)" fontFamily="JetBrains Mono" fontWeight="500">+36</text>
        <text x="530" y="36" fontSize="11" fill="var(--teal-ink)" fontFamily="JetBrains Mono" fontWeight="500">+44</text>
        <path d="M 150 94 Q 335 98 520 99" fill="none" stroke="var(--ink-40)" strokeWidth="1.5" strokeDasharray="4 3" />
        <circle cx="150" cy="94" r="4" fill="var(--ink-40)" />
        <circle cx="520" cy="99" r="4" fill="var(--ink-40)" />
        <text x="140" y="108" textAnchor="end" fontSize="11" fill="var(--ink-60)" fontFamily="JetBrains Mono">+28</text>
        <text x="530" y="113" fontSize="11" fill="var(--ink-60)" fontFamily="JetBrains Mono">+27</text>
      </svg>
    </ChartFrame>
  );
}

function QualBars() {
  // Read real theme counts from the qual JSON produced by the ETL.
  const qual = useMgQual();
  const palette = ["var(--coral)", "var(--amber)", "var(--teal)", "var(--rose)", "var(--ink-40)", "var(--ink-20)"];
  if (!qual) {
    return (
      <ChartFrame title="Loading theme counts from 18 depth interviews…">
        <div style={{ height: 180 }} />
      </ChartFrame>
    );
  }
  const total = qual.meta.total_interviews;
  const items = qual.theme_rank.slice(0, 8).map((t, i) => ({
    label: t.theme,
    count: t.respondents_mentioning,
    of: total,
    color: palette[i % palette.length],
  }));
  return (
    <ChartFrame title={`Themes across all ${total} depth interviews (3 cities · Kolkata, Lucknow, Gorakpur)`}>
      <svg viewBox={`0 0 580 ${40 + items.length * 32}`} width="100%" height={30 + items.length * 32} style={{ marginTop: 8 }}>
        {items.map((it, i) => {
          const y = 10 + i * 32;
          const w = total ? (it.count / total) * 320 : 0;
          return (
            <g key={it.label}>
              <text x="0" y={y + 13} fontSize="12" fill="var(--ink-2)" fontWeight={500}>{it.label}</text>
              <rect x="240" y={y} width="320" height="16" rx="3" fill="var(--page-2)" />
              <rect x="240" y={y} width={w} height="16" rx="3" fill={it.color} />
              <text x={240 + w + 8} y={y + 12} fontSize="11" fill="var(--ink-2)" fontFamily="JetBrains Mono" fontWeight={500}>
                {it.count} of {it.of}
              </text>
            </g>
          );
        })}
      </svg>
    </ChartFrame>
  );
}

function SegmentBars() {
  const segments = [
    { name: "Aspirational Organisers", intent: 34, share: 21, color: "var(--teal)" },
    { name: "Premium Seekers",         intent: 18, share: 14, color: "#3E8E83" },
    { name: "Pragmatic Replacers",     intent: 12, share: 28, color: "var(--ink-40)" },
    { name: "Tradition Keepers",       intent: 8,  share: 22, color: "var(--amber)" },
    { name: "Budget Stretchers",       intent: 4,  share: 15, color: "var(--ink-20)" },
  ];
  const max = 34;
  return (
    <ChartFrame title="6-month purchase intent by segment" legend={
      <>
        <span><i style={{ background: "var(--teal)" }} />Intent %</span>
        <span><i style={{ background: "var(--ink-20)", height: 6, width: 6, borderRadius: 999 }} />Segment size</span>
      </>
    }>
      <svg viewBox="0 0 620 236" width="100%" height="226" style={{ marginTop: 8 }}>
        {segments.map((s, i) => {
          const y = 10 + i * 42;
          const w = (s.intent / max) * 340;
          return (
            <g key={s.name}>
              <text x="0" y={y + 14} fontSize="12.5" fill="var(--ink-2)" fontWeight={500}>{s.name}</text>
              <rect x="220" y={y} width="340" height="18" rx="4" fill="var(--page-2)" />
              <rect x="220" y={y} width={w} height="18" rx="4" fill={s.color} />
              <text x={220 + w + 8} y={y + 14} fontSize="12" fill="var(--ink)" fontFamily="JetBrains Mono" fontWeight={600}>{s.intent}%</text>
              <text x="0" y={y + 30} fontSize="10.5" fill="var(--ink-60)">{s.share}% of sample</text>
            </g>
          );
        })}
        <line x1="356" y1="4" x2="356" y2="222" stroke="var(--ink-40)" strokeDasharray="3 3" />
        <text x="360" y="12" fontSize="10" fill="var(--ink-60)" fontFamily="JetBrains Mono">cat. avg 11%</text>
      </svg>
    </ChartFrame>
  );
}

function Divergence() {
  // 2x2 agreement matrix, styled as tiles
  return (
    <ChartFrame title="Social vs Survey sentiment agreement · Preethi · 90d">
      <div style={{
        display: "grid",
        gridTemplateColumns: "120px 1fr 1fr",
        gridTemplateRows: "24px 1fr 1fr",
        gap: 6,
        marginTop: 10,
        fontSize: 11,
      }}>
        <div />
        <div style={{ textAlign: "center", color: "var(--ink-60)", fontWeight: 500 }}>Social +</div>
        <div style={{ textAlign: "center", color: "var(--ink-60)", fontWeight: 500 }}>Social −</div>

        <div style={{ color: "var(--ink-60)", fontWeight: 500, alignSelf: "center" }}>Survey +</div>
        <div style={{ padding: "14px 16px", background: "var(--teal-tint)", border: "1px solid var(--teal-soft)", borderRadius: 6 }}>
          <div style={{ fontSize: 22, fontWeight: 500, fontFamily: "JetBrains Mono", color: "var(--teal-ink)" }}>1,842</div>
          <div style={{ fontSize: 11.5, color: "var(--ink-60)", marginTop: 2 }}>Agree · positive</div>
        </div>
        <div style={{ padding: "14px 16px", background: "var(--coral-tint)", border: "2px solid var(--coral)", borderRadius: 6, boxShadow: "0 2px 8px -4px rgba(194,82,57,0.3)" }}>
          <div style={{ fontSize: 22, fontWeight: 500, fontFamily: "JetBrains Mono", color: "var(--coral-ink)" }}>246</div>
          <div style={{ fontSize: 11.5, color: "var(--coral-ink)", marginTop: 2, fontWeight: 500 }}>Disagree · social low, survey high</div>
        </div>

        <div style={{ color: "var(--ink-60)", fontWeight: 500, alignSelf: "center" }}>Survey −</div>
        <div style={{ padding: "14px 16px", background: "var(--coral-tint)", border: "2px solid var(--coral)", borderRadius: 6, boxShadow: "0 2px 8px -4px rgba(194,82,57,0.3)" }}>
          <div style={{ fontSize: 22, fontWeight: 500, fontFamily: "JetBrains Mono", color: "var(--coral-ink)" }}>412</div>
          <div style={{ fontSize: 11.5, color: "var(--coral-ink)", marginTop: 2, fontWeight: 500 }}>Disagree · flagged for review</div>
        </div>
        <div style={{ padding: "14px 16px", background: "var(--page-2)", border: "1px solid var(--line)", borderRadius: 6 }}>
          <div style={{ fontSize: 22, fontWeight: 500, fontFamily: "JetBrains Mono", color: "var(--ink-2)" }}>618</div>
          <div style={{ fontSize: 11.5, color: "var(--ink-60)", marginTop: 2 }}>Agree · negative</div>
        </div>
      </div>
      <div style={{ marginTop: 12, fontSize: 11.5, color: "var(--ink-60)", display: "flex", gap: 16 }}>
        <span>Total Preethi mentions: <b style={{ color: "var(--ink)", fontFamily: "JetBrains Mono" }}>3,118</b></span>
        <span>Flagged: <b style={{ color: "var(--coral-ink)", fontFamily: "JetBrains Mono" }}>658 (21%)</b></span>
      </div>
    </ChartFrame>
  );
}
